// src/components/Reports.js
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
  LineChart, Line, ResponsiveContainer
} from 'recharts';
import { 
  Download, Filter, Calendar, TrendingUp, Users, Syringe 
} from 'lucide-react';
import './Reports.css';

const Reports = ({ patients }) => {
  // Calculate data for charts
  const chartData = useMemo(() => {
    if (!patients || patients.length === 0) return [];

    // Vaccine type distribution
    const vaccineTypes = {};
    patients.forEach(patient => {
      patient.vaccines?.forEach(vaccine => {
        vaccineTypes[vaccine.name] = (vaccineTypes[vaccine.name] || 0) + 1;
      });
    });

    return Object.entries(vaccineTypes).map(([name, count]) => ({
      name,
      count,
      fill: getColorForVaccine(name)
    }));
  }, [patients]);

  // Monthly vaccination data
  const monthlyData = useMemo(() => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    return months.map(month => ({
      month,
      vaccinations: Math.floor(Math.random() * 20) + 5, // Sample data
      patients: Math.floor(Math.random() * 15) + 3
    }));
  }, []);

  // Status distribution
  const statusData = useMemo(() => {
    const statusCount = {
      completed: 0,
      pending: 0,
      scheduled: 0
    };

    patients.forEach(patient => {
      patient.vaccines?.forEach(vaccine => {
        statusCount[vaccine.status] = (statusCount[vaccine.status] || 0) + 1;
      });
    });

    return [
      { name: 'Completed', value: statusCount.completed, color: '#10b981' },
      { name: 'Pending', value: statusCount.pending, color: '#f59e0b' },
      { name: 'Scheduled', value: statusCount.scheduled, color: '#3b82f6' }
    ].filter(item => item.value > 0);
  }, [patients]);

  // Patient age distribution
  const ageData = useMemo(() => {
    const ageGroups = {
      '0-18': 0,
      '19-35': 0,
      '36-50': 0,
      '51-65': 0,
      '65+': 0
    };

    patients.forEach(patient => {
      const age = patient.age;
      if (age <= 18) ageGroups['0-18']++;
      else if (age <= 35) ageGroups['19-35']++;
      else if (age <= 50) ageGroups['36-50']++;
      else if (age <= 65) ageGroups['51-65']++;
      else ageGroups['65+']++;
    });

    return Object.entries(ageGroups).map(([ageGroup, count]) => ({
      ageGroup,
      count,
      fill: getColorForAgeGroup(ageGroup)
    }));
  }, [patients]);

  function getColorForVaccine(vaccineName) {
    const colors = {
      'Influenza': '#14b8a6', // Teal
      'COVID-19': '#0a2540', // Deep Navy
      'HPV': '#8b5cf6', // Violet
      'Hepatitis B': '#10b981', // Emerald
      'MMR': '#f59e0b', // Amber
      'Tetanus': '#4f46e5', // Indigo
      'Pneumococcal': '#06b6d4', // Cyan
      'Shingles': '#f97316' // Orange
    };
    return colors[vaccineName] || '#64748b';
  }

  function getColorForAgeGroup(ageGroup) {
    const colors = {
      '0-18': '#14b8a6', // Teal
      '19-35': '#10b981', // Emerald
      '36-50': '#0a2540', // Navy
      '51-65': '#f59e0b', // Amber
      '65+': '#8b5cf6' // Violet
    };
    return colors[ageGroup] || '#64748b';
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const exportReports = () => {
    // Simulate report export
    alert('Exporting reports... This would generate PDF/CSV files in a real application.');
  };

  return (
    <motion.div 
      className="reports"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div className="reports-header" variants={itemVariants}>
        <div className="header-content">
          <h1>Analytics & Reports</h1>
          <p>Comprehensive insights and vaccination analytics</p>
        </div>
        <div className="header-actions">
          <motion.button 
            className="export-btn"
            onClick={exportReports}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Download size={18} />
            Export Reports
          </motion.button>
          <motion.button 
            className="filter-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Filter size={18} />
            Filters
          </motion.button>
        </div>
      </motion.div>

      {/* Key Metrics */}
      <motion.div className="metrics-grid" variants={containerVariants}>
        <motion.div className="metric-card" variants={itemVariants}>
          <div className="metric-icon total">
            <Users size={24} />
          </div>
          <div className="metric-content">
            <h3>{patients.length}</h3>
            <p>Total Patients</p>
            <span className="metric-trend positive">+12% this month</span>
          </div>
        </motion.div>

        <motion.div className="metric-card" variants={itemVariants}>
          <div className="metric-icon vaccinations">
            <Syringe size={24} />
          </div>
          <div className="metric-content">
            <h3>{patients.reduce((acc, p) => acc + (p.vaccines?.length || 0), 0)}</h3>
            <p>Total Vaccinations</p>
            <span className="metric-trend positive">+8% this month</span>
          </div>
        </motion.div>

        <motion.div className="metric-card" variants={itemVariants}>
          <div className="metric-icon completion">
            <TrendingUp size={24} />
          </div>
          <div className="metric-content">
            <h3>
              {patients.length > 0 
                ? Math.round((patients.filter(p => p.vaccines?.length > 0).length / patients.length) * 100)
                : 0
              }%
            </h3>
            <p>Vaccination Rate</p>
            <span className="metric-trend positive">+5% this month</span>
          </div>
        </motion.div>

        <motion.div className="metric-card" variants={itemVariants}>
          <div className="metric-icon pending">
            <Calendar size={24} />
          </div>
          <div className="metric-content">
            <h3>
              {patients.reduce((acc, p) => 
                acc + (p.vaccines?.filter(v => v.status === 'pending').length || 0), 0
              )}
            </h3>
            <p>Pending Doses</p>
            <span className="metric-trend negative">Attention needed</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Vaccine Distribution */}
        <motion.div className="chart-card" variants={itemVariants}>
          <div className="chart-header">
            <h3>Vaccine Distribution</h3>
            <p>Types of vaccines administered</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name="Vaccines Administered">
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Status Distribution */}
        <motion.div className="chart-card" variants={itemVariants}>
          <div className="chart-header">
            <h3>Vaccination Status</h3>
            <p>Distribution by completion status</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Monthly Trends */}
        <motion.div className="chart-card" variants={itemVariants}>
          <div className="chart-header">
            <h3>Monthly Trends</h3>
            <p>Vaccinations over time</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="vaccinations" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  name="Vaccinations"
                />
                <Line 
                  type="monotone" 
                  dataKey="patients" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  name="Patients"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Age Distribution */}
        <motion.div className="chart-card" variants={itemVariants}>
          <div className="chart-header">
            <h3>Age Distribution</h3>
            <p>Patients by age group</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="ageGroup" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name="Patients">
                  {ageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <motion.div className="summary-card" variants={itemVariants}>
          <h4>Top Vaccines</h4>
          <div className="vaccine-list">
            {chartData.slice(0, 3).map((vaccine, index) => (
              <div key={vaccine.name} className="vaccine-item">
                <span className="vaccine-rank">#{index + 1}</span>
                <span className="vaccine-name">{vaccine.name}</span>
                <span className="vaccine-count">{vaccine.count} doses</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div className="summary-card" variants={itemVariants}>
          <h4>Quick Stats</h4>
          <div className="stats-list">
            <div className="stat-item">
              <span>Average Vaccines per Patient</span>
              <span className="stat-value">
                {patients.length > 0 
                  ? (patients.reduce((acc, p) => acc + (p.vaccines?.length || 0), 0) / patients.length).toFixed(1)
                  : '0'
                }
              </span>
            </div>
            <div className="stat-item">
              <span>Most Common Age Group</span>
              <span className="stat-value">
                {ageData.length > 0 
                  ? ageData.reduce((max, item) => item.count > max.count ? item : max).ageGroup
                  : 'N/A'
                }
              </span>
            </div>
            <div className="stat-item">
              <span>Completion Rate</span>
              <span className="stat-value">
                {statusData.find(s => s.name === 'Completed')?.value || 0} / {patients.reduce((acc, p) => acc + (p.vaccines?.length || 0), 0)}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Reports;